import { formatDate } from '@startlibs/utils'
import { dateMessages } from './dateMessages'
import { getFileNameAndExtension, getUUID } from './common'
import {RecordClass, RecordFormat, RecordStatus} from './recordEnums'

const getType = recordClass => recordClass

export const isMultiFile = record =>
  [RecordFormat.Mirax, RecordFormat.HamamatsuVMS, RecordFormat.HamamatsuVMU].indexOf(
    record.recordFormat
  ) >= 0

export const isDicom = record => record.recordFormat === RecordFormat.DicomStudy

const canView = (recordClass, fileExtension) =>
  recordClass !== RecordClass.Other ||
  ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].indexOf(
    (fileExtension || '').toLowerCase()
  ) >= 0

const getStatus = (state, quarantined) => {
  if (quarantined) return RecordStatus.Failed
  return RecordStatus.Uploaded
}

const getActions = (state, quarantined, hasMRN) => {
  if (hasMRN) {
    if (quarantined) {
      return 'delete'
    }
    if (state === RecordStatus.Uploaded) {
      return 'complete'
    }
    return 'readonly'
  }
  return 'none'
}

export const formatRecord = record => {
  const {
    recordClass,
    format,
    fileExtension,
    info: { description, studyUID, studyDescription },
    whenCreatedEpochMilli,
    recordUID,
    quarantined,
    state,
    originalFileName,
  } = record

  const status = getStatus(state, quarantined)

  return {
    name:
      studyDescription ||
      studyUID ||
      (isMultiFile({ recordFormat: format }) ? description : originalFileName),
    type: getType(recordClass, format),
    recordClass,
    recordFormat: format,
    date: formatDate(new Date(whenCreatedEpochMilli), 'L dd, yyyy', dateMessages, true),
    sortDate: whenCreatedEpochMilli,
    progress: null,
    status,
    showDialog: quarantined,
    dialogVariant: quarantined ? 'warning' : undefined,
    actions: getActions(state, quarantined),
    quarantined,
    studyUID,
    recordUID,
    viewEnabled: canView(recordClass, fileExtension),
    key: studyUID || recordUID,
  }
}

const DEFAULT_PATHOLOGY_METADATA = {
  External_Institution: '',
  Referring_Physician: '',
  Clinical_Diagnosis_and_History: '',
  Path_Case_Number: '',
  Stain: '',
  Part_Number_and_Description: '',
}

const getMetadata = recordClass =>
  recordClass === RecordClass.Pathology ? DEFAULT_PATHOLOGY_METADATA : undefined

export const createNewRecord = (name, recordClass, recordFormat, files, extraProperties = {}) => ({
  name,
  type: getType(recordClass, recordFormat),
  recordClass,
  recordFormat,
  files,
  metadata: getMetadata(recordClass),
  date: formatDate(new Date(), 'L dd, yyyy', dateMessages, true),
  sortDate: new Date(),
  progress: null,
  status: RecordStatus.Waiting,
  actions: 'delete',
  viewEnabled: canView(recordClass, getFileNameAndExtension(files[0].file)[1]),
  key: extraProperties.studyUID || getUUID(),
  ...extraProperties,
  params: {
    class: recordClass,
    format: recordFormat,
    ...extraProperties.params,
  },
})

export const createCompliantDicomRecord = (dicomInstance, files) => {

  return createNewRecord(
    dicomInstance.studyDescription || dicomInstance.studyUID,
    RecordClass.Radiology,
    RecordFormat.DicomStudy,
    files,
    {
      studyDescription: dicomInstance.studyDescription,
      studyDate: dicomInstance.studyDate,
      patientName:dicomInstance.patientName,
      patientSex:dicomInstance.patientSex,
      patientDoB:dicomInstance.patientDoB,
      studyModalities:[dicomInstance.seriesModality],
      studyUID: dicomInstance.studyUID,
      institutionName: dicomInstance.seriesInstitutionName,
      params: {
        studyUID: dicomInstance.studyUID,
        class: RecordClass.Radiology,
        format: RecordFormat.DicomStudy,
      },
    }

  )
//  return []
}

export const createCompliantDicomRecordFromNonCompliant = (dicomInstance, files) => {
  
  function generateRandomUid(base) {
    const now = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `${base}.${now}.${random}`;
  }
  
  const safeGetModality = (instance) => {
    return instance.seriesModality ? [instance.seriesModality] : [];
  };

  const studyUIDBase = '1.3.6.1.4.1.5962.1.2.0'; // Example base UID for study
  const instanceUIDBase = '1.3.6.1.4.1.5962.1.1.0.0.0'; // Example base UID for instance

  const randomStudyUID = generateRandomUid(studyUIDBase);
  const randomInstanceUID = generateRandomUid(instanceUIDBase);

  const studyUID = dicomInstance.studyUID || randomStudyUID;
  const instanceUID = dicomInstance.instanceUID || randomInstanceUID;

  // Use a randomStudyUID if studyDescription and studyUID are not available
  const recordName = dicomInstance.studyDescription || dicomInstance?.studyUID || studyUID;


  return createNewRecord(
    recordName,
    RecordClass.Radiology,
    RecordFormat.DicomStudy,
    files,
    {
      studyDescription: dicomInstance.studyDescription || 'Unknown Description',
      studyDate: dicomInstance.studyDate || 'Unknown Date',
      patientName: dicomInstance.patientName || 'Unknown Patient',
      patientSex: dicomInstance.patientSex || '', // U for Unknown
      patientDoB: dicomInstance.patientDoB || '',
      studyModalities: safeGetModality(dicomInstance),
      studyUID: dicomInstance.studyUID || studyUID,
      institutionName: dicomInstance.seriesInstitutionName || 'Unknown',
      params: {
        studyUID: dicomInstance.studyUID || studyUID,
        class: RecordClass.Radiology,
        format: RecordFormat.DicomStudy,
      },
    }
  );
}